import { cn } from '@eo/shared';

import { ROLE_BAR_CLASS } from './constants';
import { formatHours, monthShare } from './helpers';
import { LoadBar } from './LoadBar';
import type { MemberCapacityProps } from './types';

// Committed client hours against the hours this person has for client work:
// net capacity times their client-service share of the month.
export function MemberCapacity({ member, progress }: MemberCapacityProps) {
  const role = member.role === 'data_team' ? 'dataTeam' : 'cfo';
  const capacity = member.clientCapacity;
  const committed = member.assigned.total;
  const logged = member.categoryActuals.csd;
  const over = committed - capacity;
  const csdShare = member.split ? member.split.csd : 100;

  return (
    <div data-testid="member-capacity" className="flex flex-col gap-3">
      <LoadBar
        label={`${formatHours(logged)} logged and ${formatHours(committed)} committed against ${formatHours(capacity)} for client work`}
        scale={Math.max(capacity, committed, logged)}
        target={capacity}
        pace={capacity * monthShare(progress)}
        segments={[
          { key: 'logged', hours: logged, className: ROLE_BAR_CLASS[role].logged },
          {
            key: 'remaining',
            hours: Math.max(0, committed - logged),
            className: ROLE_BAR_CLASS[role].remaining,
          },
        ]}
      />
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm tabular-nums">
        <span className="text-text-secondary">
          {formatHours(logged)} logged · {formatHours(committed)} committed of{' '}
          {formatHours(capacity)} for client work
        </span>
        <span className={cn(over > 0 ? 'text-destructive' : 'text-text-tertiary')}>
          {over > 0 ? `${formatHours(over)} over` : `${formatHours(-over)} open`}
        </span>
      </div>
      <p className="text-xs text-text-tertiary">
        {formatHours(member.capacity.netHours)} net × {csdShare}% client work. The solid tick is
        capacity; the dotted tick is where logging should be today.
      </p>
    </div>
  );
}
